import React, { useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import {
  COHORT_START_DATE,
  CTA_LABEL,
  CTA_SUBLINE,
  WHATSAPP_NUMBER,
} from "./config";

const getLocalDate = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().split("T")[0];
};

const BookingWidget = () => {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [preferredDate, setPreferredDate] = useState(getLocalDate);

  const handleSubmit = (event) => {
    event.preventDefault();

    const formattedDate = new Intl.DateTimeFormat("en", {
      dateStyle: "long",
      timeZone: "UTC",
    }).format(new Date(`${preferredDate}T00:00:00Z`));
    const message = [
      "Hi Florian, I'd like to request a free 30-minute exit call.",
      "",
      `Email: ${email}`,
      `Phone: ${phone}`,
      `Preferred date: ${formattedDate}`,
    ].join("\n");

    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );

    // Resend notification email (fire-and-forget)
    fetch("/api/send-notification", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, type: "booking", phone, preferredDate: formattedDate }),
    }).catch(() => {});
  };

  return (
    <Box
      id="booking-request-form"
      component="form"
      onSubmit={handleSubmit}
      sx={{
        bgcolor: "rgba(255, 255, 255, 0.05)",
        borderRadius: 4,
        p: { xs: 2.5, sm: 4 },
        my: 3,
        mx: "auto",
        maxWidth: 620,
        border: "1px solid rgba(245, 182, 66, 0.3)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        backdropFilter: "blur(10px)",
        colorScheme: "dark",
        "& .MuiInputLabel-root": {
          color: "rgba(255, 255, 255, 0.82)",
        },
        "& .MuiInputLabel-root.Mui-focused": {
          color: "secondary.main",
        },
        "& .MuiInputBase-input": {
          color: "#fff",
          WebkitTextFillColor: "#fff",
        },
        "& .MuiFormHelperText-root": {
          color: "rgba(255, 255, 255, 0.72)",
        },
        "& .MuiOutlinedInput-root": {
          bgcolor: "rgba(0, 0, 0, 0.16)",
          "& fieldset": {
            borderColor: "rgba(255, 255, 255, 0.38)",
          },
          "&:hover fieldset": {
            borderColor: "rgba(255, 255, 255, 0.72)",
          },
          "&.Mui-focused fieldset": {
            borderColor: "secondary.main",
          },
        },
      }}
    >
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Request Your Free Call
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, opacity: 0.85 }}>
        Share your details and preferred date. We’ll confirm the exact time with you on WhatsApp.
      </Typography>

      <Box sx={{ display: "grid", gap: 2.5 }}>
        <TextField
          label="Email address"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          fullWidth
          autoComplete="email"
        />
        <TextField
          label="Phone number"
          type="tel"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          required
          fullWidth
          autoComplete="tel"
          helperText="Please include your country code."
        />
        <TextField
          label="Preferred date"
          type="date"
          value={preferredDate}
          onChange={(event) => setPreferredDate(event.target.value)}
          required
          fullWidth
          helperText="Choose any date from today until the countdown ends."
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: {
              min: getLocalDate(),
              max: COHORT_START_DATE,
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          color="secondary"
          size="large"
          startIcon={<CalendarMonth />}
          sx={{ py: 1.75, fontSize: "1.05rem" }}
        >
          {CTA_LABEL}
        </Button>
      </Box>

      <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
        {CTA_SUBLINE} Submitting opens WhatsApp with your request ready to send.
      </Typography>
    </Box>
  );
};

export default BookingWidget;
